import React from 'react';
import { Link } from 'react-router-dom';
import { Seo } from '../shared';
import ExploreHero from '../explore/ExploreHero';
import QuickFactsBar from '../explore/QuickFactsBar';
import DiscoverySection from '../explore/DiscoverySection';
import { KNOWLEDGE_INDEX_META, KNOWLEDGE_TOPICS, statusIcon, experienceLabel } from '../../data/knowledgeIndexData';
import { EXPLORE_IMAGES } from '../../data/exploreImages';
import '../../styles/explore.scss';

function KnowledgeIndexPage() {
	const sorted = [...KNOWLEDGE_TOPICS].sort((a, b) => b.coverage - a.coverage);
	const critical = KNOWLEDGE_TOPICS.filter((t) => t.status === 'critical_gap');
	const strong = KNOWLEDGE_TOPICS.filter((t) => t.coverage >= 50);

	return (
		<div className="explore-page">
			<Seo
				title="Nepal Knowledge Index — Content Coverage"
				path="/knowledge-index"
				noindex
				description="VisitAllNepal knowledge quality index — coverage percentage, source quality, and gaps for every Nepal travel topic."
			/>
			<ExploreHero
				image={EXPLORE_IMAGES.valley}
				eyebrow="Editorial Roadmap"
				title="Nepal Knowledge Index"
				lead="How complete is VisitAllNepal as a Nepal travel encyclopedia? This index tracks coverage, source quality, and open gaps — updated after every research pass."
				compact
			/>
			<QuickFactsBar facts={[
				{ value: `${KNOWLEDGE_INDEX_META.score}/100`, label: 'Platform score' },
				{ value: String(strong.length), label: 'Topics ≥50%' },
				{ value: String(critical.length), label: 'Critical gaps' },
				{ value: KNOWLEDGE_INDEX_META.lastAudit, label: 'Last audit' },
			]} />

			<div className="container" style={{ marginTop: '-1rem', marginBottom: '1.5rem' }}>
				<p className="text-muted small mb-0">
					Topic depth (visa, safety, food) lives here. Geographic breadth (77 districts, parks, villages) lives in the{' '}
					<Link to="/coverage-map">Nepal Coverage Map →</Link>
				</p>
			</div>

			<div className="explore-body">
				<DiscoverySection title="Coverage by topic" intro="Benchmark: a first-time traveler plans and completes a Nepal trip without essential Googling.">
					<div className="knowledge-index-table-wrap">
						<table className="knowledge-index-table">
							<thead>
								<tr>
									<th>Topic</th>
									<th>Coverage</th>
									<th>Sources</th>
									<th>Last verified</th>
									<th>Experience</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>
								{sorted.map((t) => (
									<tr key={t.id} className={`knowledge-index-table__row--${t.status}`}>
										<td>
											<Link to={t.route} className="knowledge-index-table__link">{t.name}</Link>
										</td>
										<td>
											<div className="knowledge-index-bar">
												<div className="knowledge-index-bar__fill" style={{ width: `${t.coverage}%` }} />
												<span>{t.coverage}%</span>
											</div>
										</td>
										<td>{t.officialRefs > 0 ? `${t.officialRefs} official` : t.sourceQuality}</td>
										<td>{t.lastVerified || '—'}</td>
										<td>{experienceLabel(t.experience)}</td>
										<td>{statusIcon(t.status)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</DiscoverySection>

				<DiscoverySection eyebrow="Priority gaps" title="Highest-impact gaps" alt>
					<ul className="arrival-persona-list">
						{critical.map((t) => (
							<li key={t.id}>
								<strong>{t.name}</strong> ({t.coverage}%) — {t.experience} traveler experience
							</li>
						))}
					</ul>
				</DiscoverySection>

				<DiscoverySection eyebrow="Strongest topics" title="Best coverage today">
					<div className="lifecycle-mini-grid">
						{strong.map((t) => (
							<Link key={t.id} to={t.route} className="lifecycle-mini-card">
								<span className="lifecycle-mini-card__name">{t.name}</span>
								<span className="lifecycle-mini-card__cov">{t.coverage}%</span>
							</Link>
						))}
					</div>
				</DiscoverySection>

				<section className="discovery-section">
					<div className="container">
						<div className="explore-cta-band">
							<h3>Plan your trip</h3>
							<p>Visa, arrival, seasons, safety — plain guides on the home page.</p>
							<Link to={{ pathname: '/', hash: '#plan' }} className="explore-hero__cta explore-hero__cta--primary">Plan topics →</Link>
						</div>
					</div>
				</section>
			</div>
		</div>
	);
}

export default KnowledgeIndexPage;
